import Button from '@rio-cloud/rio-uikit/Button';
import SimpleDialog from '@rio-cloud/rio-uikit/SimpleDialog';
import type { FunctionComponent, JSX } from 'react';
import { useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector, useAssetsThunkDispatch } from '../../../../../configuration/setup/hooks';
import { fetchAssets, fetchDevices } from '../../../actions/assets/Assets.actions';
import {
    closeEasyOnboardingDialog,
    getCloseDialogTriggersReload,
    getConnectorDialogTitleOverwrite,
    setIsAssetReloadImminent,
} from '../../../reducers/assets/assetsSlice';
import { FormStep } from './EasyOnboardingDialog';
import type { AssetTypeTile, BrandTile, TelematicsTile } from './configuration/types';

interface SimpleDialogWrapperProps {
    formStep: FormStep;
    selectedAssetType: AssetTypeTile | undefined;
    selectedBrand: BrandTile | undefined;
    selectedTelematics: TelematicsTile | undefined;
    dialogContent: JSX.Element;
}

export const SimpleDialogWrapper: FunctionComponent<SimpleDialogWrapperProps> = props => {
    const intl = useIntl();
    const showDialog = useAppSelector(state => state.assets.easyOnboardingDialog.show);
    const connectorDialogTitleOverwrite = useAppSelector(getConnectorDialogTitleOverwrite);
    const isTriggerReloadOnClose = useAppSelector(getCloseDialogTriggersReload);
    const dispatch = useAppDispatch();
    const assetsThunkDispatch = useAssetsThunkDispatch();
    const closeDialog = () => {
        dispatch(closeEasyOnboardingDialog());
        if (isTriggerReloadOnClose) {
            dispatch(setIsAssetReloadImminent(true));
            setTimeout(
                () =>
                    assetsThunkDispatch(fetchAssets({ force: true })).then(() =>
                        assetsThunkDispatch(fetchDevices({ force: true }))
                    ),
                2000
            );
        }
    };

    const getTitleId = () => {
        switch (props.formStep) {
            case FormStep.selectAssetType:
                return 'assets.assets.create-new-dialog.type.message';
            case FormStep.selectBrand:
                return props.selectedAssetType
                    ? `assets.assets.create-new-dialog.brand.message.${props.selectedAssetType.assetType}`
                    : 'assets.assets.create-new-dialog.brand.message.fallback';
            case FormStep.selectTelematics:
                return props.selectedAssetType
                    ? `assets.assets.create-new-dialog.telematics.message.${props.selectedAssetType.assetType}`
                    : 'assets.assets.create-new-dialog.telematics.message.fallback';
            case FormStep.connector:
                if (connectorDialogTitleOverwrite) {
                    return connectorDialogTitleOverwrite;
                }
                return 'assets.assets.create-new-dialog.connector.message';
            default:
                return '';
        }
    };
    const titleId = getTitleId();

    return (
        <SimpleDialog
            show={showDialog}
            bodyClassName={'padding-0 bg-lightest'}
            title={intl.formatMessage({ id: titleId })}
            content={props.dialogContent}
            onClose={closeDialog}
            showCloseButton={false}
            headerButtons={
                <Button
                    bsStyle={Button.MUTED}
                    iconOnly
                    iconName='rioglyph-remove'
                    autoTrackingKey={`${titleId}.close`}
                    className='modal-header-close close'
                    onClick={closeDialog}
                    data-testid='close'
                    aria-label='dialog close button'
                />
            }
        />
    );
};
